import { ApiCall } from '@dto/api-calls.dto';
import { AllEmailStatsWhenSentPeriod } from '@dto/rates/email-stats-when-sent.dto';
import { AllProvidersStatsPeriod } from '@dto/rates/providers-stats';
import { ThresholdByProductId } from '@dto/threshold/threshold.dto';
import { HistogramMailSent } from '@entity/charts/histogram-sent-mail.entity';
import { FilterData } from '@entity/filter-data.entity';
import { createReducer, on } from '@ngrx/store';
import {
  changeDashboardFilters,
  changeDashboardFiltersSuccess,
  retrieveDeliverabilityIndicators,
  retrieveDeliverabilityIndicatorsFailure,
  retrieveDeliverabilityIndicatorsSuccess,
  retrieveHistogramData,
  retrieveHistogramDataFailure,
  retrieveHistogramDataSuccess,
  retrieveProvidersStatsData,
  retrieveProvidersStatsDataFailure,
  retrieveProvidersStatsDataSuccess,
  retrieveThresholdData,
  retrieveThresholdDataFailure,
  retrieveThresholdDataSuccess
} from '@store/dashboard/dashboard.action';

export const defaultDashboardFilter: FilterData = {
  productId: [],
  eventType: [],
  period: {
    from: new Date(
      new Date().getFullYear(),
      new Date().getMonth(),
      new Date().getDate() - 8
    ).getTime(), // 8 last full days
    to: new Date().getTime(),
  },
};

export interface DashboardState {
  dashboardFilter: FilterData;
  chartData: {
    histogramMailSent: ApiCall<HistogramMailSent>;
    deliverabilityIndicators: ApiCall<AllEmailStatsWhenSentPeriod>;
    providersStats: ApiCall<AllProvidersStatsPeriod>;
    thresholds: ApiCall<ThresholdByProductId>;
  };
}

export const initialState: DashboardState = {
  dashboardFilter: defaultDashboardFilter,
  chartData: {
    histogramMailSent: { data: { currentGranularity: 'DAY', data: {} }, isLoading: false, error: null },
    deliverabilityIndicators: { data: { allOnPeriod: {}, yesterday: {}, lastSevenFullDays: {} }, isLoading: false, error: null },
    providersStats: { data: { allOnPeriod: {}, yesterday: {} }, isLoading: false, error: null },
    thresholds: { data: {}, isLoading: false, error: null },
  }
};

export const dashboardReducer = createReducer(
  initialState,

  // Dashboard filters
  on(changeDashboardFilters, (state) => state),
  on(changeDashboardFiltersSuccess, (state, { filters }) => ({
    ...state,
    dashboardFilter: {
      ...filters,
      period: {
        from: filters.period.from,
        to: filters.period.to,
      },
    },
  })),
  on(retrieveHistogramData, (state) => ({
    ...state,
    chartData: {
      ...state.chartData,
      histogramMailSent: {
        ...state.chartData.histogramMailSent,
        isLoading: true,
      },
    },
  })),
  on(retrieveHistogramDataSuccess, (state, { histogramData }) => ({
    ...state,
    chartData: {
      ...state.chartData,
      histogramMailSent: {
        data: histogramData,
        isLoading: false,
        error: null,
      },
    },
  })
  ),
  on(retrieveHistogramDataFailure, (state, { error }) => ({
    ...state,
    chartData: {
      ...state.chartData,
      histogramMailSent: {
        ...state.chartData.histogramMailSent,
        isLoading: false,
        error,
      },
    },
  })
  ),
  // Deliverability Indicators
  on(retrieveDeliverabilityIndicators, (state) => ({
    ...state,
    chartData: {
      ...state.chartData,
      deliverabilityIndicators: {
        ...state.chartData.deliverabilityIndicators,
        isLoading: true,
      },
    },
  })),
  on(retrieveDeliverabilityIndicatorsSuccess, (state, { allOnPeriod, yesterday, lastSevenFullDays }) => ({
    ...state,
    chartData: {
      ...state.chartData,
      deliverabilityIndicators: {
        data: {
          allOnPeriod,
          yesterday,
          lastSevenFullDays
        },
        isLoading: false,
        error: null,
      },
    },
  })
  ),
  on(retrieveDeliverabilityIndicatorsFailure, (state, { error }) => ({
    ...state,
    chartData: {
      ...state.chartData,
      deliverabilityIndicators: {
        ...state.chartData.deliverabilityIndicators,
        isLoading: false,
        error,
      },
    },
  })
  ),
  // Thresholds
  on(retrieveThresholdData, (state) => ({
    ...state,
    chartData: {
      ...state.chartData,
      thresholds: {
        ...state.chartData.thresholds,
        isLoading: true,
      },
    },
  })),
  on(retrieveThresholdDataSuccess, (state, { thresholdData }) => ({
    ...state,
    chartData: {
      ...state.chartData,
      thresholds: {
        data: thresholdData,
        isLoading: false,
        error: null,
      },
    },
  })
  ),
  on(retrieveThresholdDataFailure, (state, { error }) => ({
    ...state,
    chartData: {
      ...state.chartData,
      thresholds: {
        ...state.chartData.thresholds,
        isLoading: false,
        error,
      },
    },
  }),
  ),

  // Providers Stats
  on(retrieveProvidersStatsData, (state) => ({
    ...state,
    chartData: {
      ...state.chartData,
      providersStats: {
        ...state.chartData.providersStats,
        isLoading: true,
      },
    },
  })),
  on(retrieveProvidersStatsDataSuccess, (state, { allOnPeriod, yesterday }) => ({
    ...state,
    chartData: {
      ...state.chartData,
      providersStats: {
        data: {
          allOnPeriod,
          yesterday
        },
        isLoading: false,
        error: null,
      },
    },
  })),
  on(retrieveProvidersStatsDataFailure, (state, { error }) => ({
    ...state,
    chartData: {
      ...state.chartData,
      providersStats: {
        ...state.chartData.providersStats,
        isLoading: false,
        error,
      },
    },
  }))
);
